/**
 * Requisiti funzionali di un progetto (ADR 0050 §6), dal servizio dei progetti.
 *
 * Un requisito ha un codice (`RF-001`) assegnato dalla piattaforma, una sezione, un titolo
 * e una descrizione con i criteri di accettazione. Gli agenti propongono le modifiche
 * (una sola proposta aperta per progetto); l'utente le approva o le rifiuta, oppure scrive
 * i requisiti da sé. Ogni verifica e2e si registra con ambito ed esito per requisito, e il
 * controllo del rilascio dice se la produzione può ricevere una versione nuova.
 *
 * Motivi, riassunti e note li scrivono gli agenti: la pagina li mostra come dati.
 *
 * Provvisorio dichiarato, come gli altri client (CLAUDE.md §2.3): percorsi e tipi scritti
 * a mano, presidiati da `requirements.test.ts`.
 */

import type { ApiClient } from '../client';

/** Chi ha scritto: un utente o un agente. */
export type AuthorKind = 'user' | 'agent';

/** Esito di un requisito in una verifica. */
export type RequirementOutcome = 'passed' | 'failed' | 'not_run';

/** Ultimo esito di un requisito. */
export interface RequirementResult {
  /** Esito. */
  outcome: RequirementOutcome;
  /** Verifica che l'ha registrato. */
  run_id: string;
  /** Istante della verifica, ISO 8601. */
  at: string;
}

/** Un requisito funzionale. */
export interface Requirement {
  /** Codice, `RF-001`…, mai riusato. */
  code: string;
  /** Area funzionale (1-80 caratteri). */
  section: string;
  /** Titolo (1-200 caratteri). */
  title: string;
  /** Comportamento e criteri di accettazione (1-4000 caratteri). */
  description: string;
  /** `active`, o `removed` per un requisito tolto che resta nello storico. */
  state: 'active' | 'removed';
  /** Revisione che l'ha scritto per ultima. */
  revision: number;
  /** Chi l'ha creato. */
  created_by: string;
  /** Utente o agente. */
  created_by_kind: AuthorKind;
  /** Chi l'ha cambiato per ultimo. */
  updated_by: string;
  /** Utente o agente. */
  updated_by_kind: AuthorKind;
  /** Creazione, ISO 8601. */
  created_at: string;
  /** Ultimo cambio, ISO 8601. */
  updated_at: string;
  /** Ultimo esito; `null` se mai verificato. */
  last_result: RequirementResult | null;
}

/** Operazione di una voce della proposta. */
export type ProposalOp = 'add' | 'modify' | 'remove';

/** Una voce di una modifica proposta. */
export interface ProposalItem {
  /** Aggiunta, modifica o rimozione. */
  op: ProposalOp;
  /** Codice del requisito toccato; `null` per un'aggiunta. */
  code: string | null;
  /** Sezione. */
  section: string;
  /** Titolo. */
  title: string;
  /** Descrizione. */
  description: string;
  /** Motivo, scritto dall'agente. */
  reason: string;
}

/** Stato di una modifica proposta. */
export type ProposalState = 'proposed' | 'applied' | 'rejected' | 'withdrawn';

/** Una modifica proposta dei requisiti. */
export interface RequirementProposal {
  /** Identificativo. */
  id: string;
  /** Stato. */
  state: ProposalState;
  /** Riassunto della richiesta, scritto dall'agente. */
  summary: string;
  /** Conflitti con i requisiti attuali, scritti dall'agente; vuoto se non ce ne sono. */
  conflicts: string;
  /** Voci. */
  items: ProposalItem[];
  /** Revisione su cui è stata scritta. */
  base_revision: number;
  /** Chi l'ha proposta. */
  proposed_by: string;
  /** Set di provenienza. */
  agent_set_id: string | null;
  /** Creazione, ISO 8601. */
  created_at: string;
  /** Chi ha deciso; `null` se ancora aperta. */
  decided_by: string | null;
  /** Utente o agente. */
  decided_by_kind: AuthorKind | null;
  /** Decisione, ISO 8601. */
  decided_at: string | null;
  /** Nota della decisione. */
  decision_note: string;
  /** Codici scritti dall'applicazione. */
  applied_codes: string[];
}

/** Ambito di una verifica e2e. */
export type TestRunScope = 'full' | 'sections' | 'skipped';

/** Esito di una verifica e2e. */
export type TestRunOutcome = 'passed' | 'failed' | 'skipped';

/** Una verifica e2e registrata. */
export interface TestRun {
  /** Identificativo. */
  id: string;
  /** Intera, di sezione o saltata. */
  scope: TestRunScope;
  /** Sezioni provate, per l'ambito `sections`. */
  sections: string[];
  /** Esito. */
  outcome: TestRunOutcome;
  /** Esito per requisito, con la nota dell'agente. */
  results: { code: string; outcome: RequirementOutcome; note: string }[];
  /** Riassunto o motivo del salto, scritto dall'agente. */
  summary: string;
  /** Commit provato. */
  commit_sha: string;
  /** Revisione dei requisiti al momento della verifica. */
  requirements_revision: number;
  /** Chi l'ha registrata. */
  actor: string;
  /** Set di provenienza. */
  agent_set_id: string | null;
  /** Registrazione, ISO 8601. */
  created_at: string;
}

/** Codici del controllo del rilascio (ADR 0050 §6.4). */
export type ReleaseCheckCode =
  | 'ok'
  | 'requirements_pending'
  | 'verification_outdated'
  | 'full_verification_required'
  | 'verification_failed'
  | 'verification_scope'
  | 'verification_missing';

/** Esito del controllo del rilascio. */
export interface ReleaseCheck {
  /** True se una finestra di rilascio si aprirebbe adesso. */
  ok: boolean;
  /** Codice dell'esito; un codice nuovo del backend arriva come stringa. */
  code: ReleaseCheckCode | string;
  /** Spiegazione per i log, in inglese: la pagina usa il codice. */
  detail: string;
  /** Verifica guardata; `null` se nessuna. */
  test_run_id: string | null;
  /** Sezioni cambiate dopo l'ultima verifica intera passata. */
  sections_changed: string[];
}

/** Requisiti del progetto con proposta, verifiche e controllo del rilascio. */
export interface ProjectRequirements {
  /** Revisione corrente dei requisiti. */
  revision: number;
  /** Requisiti attivi. */
  requirements: Requirement[];
  /** Requisiti tolti. */
  removed: Requirement[];
  /** Modifica proposta aperta; `null` se non c'è. */
  proposal: RequirementProposal | null;
  /** Verifiche, dalla più recente. */
  test_runs: TestRun[];
  /** Controllo del rilascio. */
  release_check: ReleaseCheck;
}

/** Corpo di un requisito scritto dall'utente. */
export interface RequirementDraft {
  /** Sezione. */
  section: string;
  /** Titolo. */
  title: string;
  /** Descrizione con i criteri di accettazione. */
  description: string;
}

/** Codici dei rifiuti delle proposte, tradotti dalla pagina. */
export const PROPOSAL_REFUSAL_CODES = ['proposal_stale', 'proposal_closed'] as const;

/**
 * Percorso dei requisiti di un progetto.
 *
 * @param projectId Progetto.
 * @param suffix Parte finale.
 * @returns Il percorso.
 */
function requirementsPath(projectId: string, suffix = ''): string {
  return `/api/v1/projects/${encodeURIComponent(projectId)}/requirements${suffix}`;
}

/** Operazioni sui requisiti, esposte alla pagina del progetto. */
export class RequirementsApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Legge i requisiti del progetto.
   *
   * @param projectId Progetto.
   * @returns Requisiti, proposta aperta, verifiche e controllo del rilascio.
   * @throws ApiError 403 senza lettura sul progetto.
   */
  get(projectId: string): Promise<ProjectRequirements> {
    return this.client.request<ProjectRequirements>('GET', requirementsPath(projectId));
  }

  /**
   * Aggiunge un requisito: la piattaforma gli dà il codice.
   *
   * @param projectId Progetto.
   * @param draft Sezione, titolo e descrizione.
   * @returns Il requisito creato.
   */
  create(projectId: string, draft: RequirementDraft): Promise<Requirement> {
    return this.client.request<Requirement>('POST', requirementsPath(projectId), draft);
  }

  /**
   * Cambia un requisito.
   *
   * @param projectId Progetto.
   * @param code Codice del requisito.
   * @param draft Sezione, titolo e descrizione nuovi.
   * @returns Il requisito cambiato.
   */
  update(projectId: string, code: string, draft: RequirementDraft): Promise<Requirement> {
    return this.client.request<Requirement>('PUT', requirementsPath(projectId, `/${encodeURIComponent(code)}`), draft);
  }

  /**
   * Toglie un requisito: resta nello storico come `removed`.
   *
   * @param projectId Progetto.
   * @param code Codice del requisito.
   * @returns Nulla.
   */
  remove(projectId: string, code: string): Promise<void> {
    return this.client.request<void>('DELETE', requirementsPath(projectId, `/${encodeURIComponent(code)}`));
  }

  /**
   * Approva la modifica proposta e la applica.
   *
   * @param projectId Progetto.
   * @param proposalId Proposta.
   * @param note Nota facoltativa.
   * @returns La proposta applicata.
   * @throws ApiError 409 `proposal_stale` se i requisiti sono cambiati dopo la proposta,
   *   `proposal_closed` se è già chiusa.
   */
  approve(projectId: string, proposalId: string, note?: string): Promise<RequirementProposal> {
    return this.client.request<RequirementProposal>(
      'POST',
      requirementsPath(projectId, `/proposals/${encodeURIComponent(proposalId)}/approve`),
      note === undefined || note === '' ? {} : { note },
    );
  }

  /**
   * Rifiuta la modifica proposta.
   *
   * @param projectId Progetto.
   * @param proposalId Proposta.
   * @param note Motivo del rifiuto, obbligatorio.
   * @returns La proposta rifiutata.
   * @throws ApiError 409 `proposal_closed` se è già chiusa.
   */
  reject(projectId: string, proposalId: string, note: string): Promise<RequirementProposal> {
    return this.client.request<RequirementProposal>(
      'POST',
      requirementsPath(projectId, `/proposals/${encodeURIComponent(proposalId)}/reject`),
      { note },
    );
  }
}
